/**
 * Field-side handler for operator-issued camera commands.
 *
 * Mounts inside <LiveKitRoom>. On track-ready, broadcasts the device's
 * capabilities once via `presence/capabilities` so the operator UI can
 * grey out unsupported controls upfront. Then registers handlers for:
 *   - `command/camera.switch` → restart the local video track with the
 *     requested facingMode (or toggle); ack with the new facing.
 *   - `command/torch.toggle` → applyConstraints torch; ack with new state
 *     or nack with a readable reason (iOS Safari, no flash hardware, etc.).
 *
 * Per AGENTS.md §10 we **never silently fail** a command — every code
 * path returns either `{ok: true, ...}` or `{ok: false, reason}`.
 */
import { useRoomContext } from "@livekit/components-react";
import {
  ConnectionQuality,
  LocalVideoTrack,
  RoomEvent,
  Track,
  TrackEvent,
} from "livekit-client";
import { useEffect } from "react";

import {
  CommandBus,
  makeLiveKitTransport,
  type Envelope,
} from "@/lib/commandBus";
import {
  applyQualityTier,
  applyTorch,
  applyZoom,
  probeCapabilities,
  switchFacing,
  upgradeCaptureResolution,
  type CaptureQualityTier,
  type FacingMode,
  type SwitchCameraAck,
  type SwitchCameraPayload,
  type ToggleAck,
  type TogglePayload,
  type ZoomAck,
  type ZoomPayload,
} from "@/lib/cameraCommands";
import { useCameraIntent } from "@/lib/cameraIntent";
import { useConnectionQualityStore } from "@/lib/connectionQuality";

type Props = { sessionId: string };

export function FieldCommandsHandler({ sessionId }: Props) {
  const room = useRoomContext();

  useEffect(() => {
    if (!room) return;
    const transport = makeLiveKitTransport(room, sessionId);
    const bus = new CommandBus(transport, sessionId, {
      kind: "participant",
      id: room.localParticipant.identity,
    });

    // Once the local camera is publishing, broadcast capabilities so the
    // operator UI can disable unsupported buttons.
    let torchOn = false;
    // The desired facing + in-flight-switch flag live in a SHARED store
    // (useCameraIntent) so BOTH the operator's camera.switch (below) AND the
    // field tech's own flip (FieldLayout.handleFlip) can set them. Otherwise
    // the auto-restore below reverts the field's flip on sight.
    const { setDesired, setSwitching } = useCameraIntent.getState();
    const getDesired = () => useCameraIntent.getState().desired;
    const getSwitching = () => useCameraIntent.getState().switching;

    // Restore HD capture after a track restart / camera switch / app-resume.
    // The recovery logic lives in cameraCommands.upgradeCaptureResolution so
    // the field tech's OWN flip (FieldLayout.handleFlip) reuses the exact same
    // path the operator-driven switch does below — see that helper for the
    // iPhone applyConstraints-accumulation lessons.
    const upgradeResolutionIfNeeded = () =>
      upgradeCaptureResolution(getLocalCameraTrack(room));

    const broadcastCapabilities = async () => {
      const track = getLocalCameraTrack(room);
      if (!track) return;
      try {
        const caps = await probeCapabilities(track.mediaStreamTrack);
        void bus.send({
          topic: "presence",
          type: "capabilities",
          payload: caps,
        });
      } catch {
        /* capabilities are an optimisation — failure leaves the operator
           sending blind, which the nack path handles fine */
      }
    };
    // Resolution upgrade fires BEFORE the first capabilities broadcast
    // so the operator UI sees the post-upgrade dimensions in the first
    // capabilities envelope (not the 480×640 default).
    void upgradeResolutionIfNeeded().then(() => broadcastCapabilities());

    // App-switch / tab-resume recovery. When the field tech backgrounds
    // the browser (answers a call, checks another app) iOS/Android
    // reclaims the camera; on return the track resumes at the 480×640
    // default (or a silent downgrade) and our one-shot startup upgrade is
    // long gone — so the video stays low-res until something else
    // re-acquires. Re-run the (idempotent, skips-if-already-HD) upgrade
    // whenever the page becomes visible again. The short delay lets the
    // OS finish re-granting the camera before we read/applyConstraints.
    let resumeTimer: ReturnType<typeof setTimeout> | undefined;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        void upgradeResolutionIfNeeded().then(() => broadcastCapabilities());
      }, 600);
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Suppress auto-restore while a user-initiated camera switch is in
    // flight. Without this flag, the sequence is a feedback loop:
    //   1. handleSwitch calls switchFacing → track restart → Track.Restarted fires
    //   2. Listener sees the new track.facingMode != desiredFacing (still
    //      the old value because handleSwitch's await hasn't returned yet)
    //   3. Listener calls restartTrack to "restore" the old facingMode
    //   4. handleSwitch's ack returns → sets desiredFacing to the new value
    //   5. The auto-restore's restart fires Track.Restarted → mismatch again
    //   6. Ping-pong forever
    //
    // The flag is set BEFORE switchFacing and cleared in finally — any
    // Track.Restarted between those two points is the user's switch
    // landing, not an external disruption, so don't fight it.
    // (`switching` now lives in the shared store — see getSwitching above.)
    //
    // Loop guard: on some devices restartTrack can't actually honor the
    // requested facingMode, so the auto-restore fires, the track comes back on
    // the wrong facing, and it restores again — forever. Cap consecutive
    // auto-restores; reset the counter whenever the facing actually matches.
    let autoRestoreStreak = 0;

    // Attach a Track.Restarted listener every time the local camera
    // publishes. If the track gets replaced (LiveKit republish,
    // adaptive-stream resume, mobile-browser camera reclaim, etc.)
    // and the resulting facingMode doesn't match the operator's
    // last choice, restartTrack to put it back. The log lines also
    // surface WHEN restarts happen so future regressions are
    // diagnosable from worker logs.
    const restartHandlers = new WeakMap<LocalVideoTrack, () => void>();
    const attachRestartListener = (track: LocalVideoTrack) => {
      if (restartHandlers.has(track)) return;
      const handler = () => {
        const got = track.mediaStreamTrack?.getSettings?.()
          ?.facingMode as FacingMode | undefined;
        const desired = getDesired();
        // eslint-disable-next-line no-console
        console.info(
          "[FieldCommandsHandler] local camera restarted: got=%s desired=%s switching=%s",
          got,
          desired,
          getSwitching(),
        );
        void broadcastCapabilities();
        // Already on the desired facing → nothing to fix; reset the guard.
        if (!desired || !got || got === desired) {
          autoRestoreStreak = 0;
          return;
        }
        // Suppress auto-restore during a deliberate switch (operator or field).
        if (getSwitching()) return;
        // Loop guard: if the device keeps landing on the wrong facing, stop
        // fighting it after a few tries (it can't honor the request).
        if (autoRestoreStreak >= 3) return;
        if (
          typeof (
            track as { restartTrack?: (c: { facingMode: FacingMode }) => Promise<void> }
          ).restartTrack === "function"
        ) {
          autoRestoreStreak += 1;
          // eslint-disable-next-line no-console
          console.warn(
            "[FieldCommandsHandler] auto-restoring facingMode %s → %s (attempt %d)",
            got,
            desired,
            autoRestoreStreak,
          );
          void (
            track as { restartTrack: (c: { facingMode: FacingMode }) => Promise<void> }
          )
            .restartTrack({ facingMode: desired })
            .catch(() => {});
        }
      };
      track.on(TrackEvent.Restarted, handler);
      restartHandlers.set(track, handler);
    };
    const liveTrack = getLocalCameraTrack(room);
    if (liveTrack) attachRestartListener(liveTrack);
    const onPublished = () => {
      // Always upgrade resolution on a fresh publish — the iPhone-
      // Safari quirk affects every back-camera acquire, not just the
      // initial one. Camera switches re-acquire the track.
      void upgradeResolutionIfNeeded().then(() => broadcastCapabilities());
      const t = getLocalCameraTrack(room);
      if (t) attachRestartListener(t);
    };
    room.on("localTrackPublished", onPublished);

    const offSwitch = bus.on<SwitchCameraPayload, SwitchCameraAck>(
      "command/camera.switch",
      async (env) => {
        // Mark a switch in flight so the Track.Restarted listener
        // doesn't fight us. Cleared after a grace period — events
        // can fire shortly AFTER the await returns.
        setSwitching(true);
        autoRestoreStreak = 0;
        let ack: SwitchCameraAck;
        try {
          // Set desiredFacing UP FRONT (before switchFacing) so even
          // if the restart event fires before our ack lands, the
          // listener sees the correct target. `toggle` requires
          // reading current facing first.
          const target: FacingMode =
            env.payload.facing === "toggle"
              ? (getDesired() === "user" ? "environment" : "user")
              : env.payload.facing;
          setDesired(target);
          ack = await handleSwitch(env, room);
        } finally {
          setTimeout(() => {
            setSwitching(false);
          }, 500);
        }
        if (ack.ok) {
          // Re-broadcast capabilities AFTER the camera swap. Front
          // cameras on most devices have no torch and a fixed lens;
          // back cameras gain torch + zoom + autofocus. Without this
          // refresh the operator UI keeps the torch button greyed
          // out forever even though the back camera supports it —
          // the original "torch never worked" symptom.
          //
          // Camera switch to back also lands in 480×640 land on iPhone
          // unless we re-apply the resolution upgrade. Same flow.
          void upgradeResolutionIfNeeded().then(() => broadcastCapabilities());
          // Reset the torchOn shadow — switching cameras hands us a
          // fresh MediaStreamTrack with torch off by default.
          torchOn = false;
          // Remember the operator's choice so the Track.Restarted
          // listener can re-apply it if LiveKit later re-acquires
          // getUserMedia at the wrong facingMode.
          if (ack.facing) setDesired(ack.facing);
          // applyConstraints (in switchFacing) preserves the
          // MediaStreamTrack so no Restarted event fires there —
          // re-attach the listener defensively to the (possibly
          // new) track in case the fallback restartTrack path
          // replaced it.
          const t = getLocalCameraTrack(room);
          if (t) attachRestartListener(t);
        }
        return ack;
      },
    );
    const offTorch = bus.on<TogglePayload, ToggleAck>(
      "command/torch.toggle",
      async (env) => {
        const track = getLocalCameraTrack(room);
        if (!track) return { ok: false, reason: "no local camera track" };
        const next = env.payload.on === "toggle" ? !torchOn : !!env.payload.on;
        try {
          await applyTorch(track.mediaStreamTrack, next);
          torchOn = next;
          return { ok: true, on: next };
        } catch (e) {
          return { ok: false, reason: (e as Error).message };
        }
      },
    );
    const offZoom = bus.on<ZoomPayload, ZoomAck>(
      "command/camera.zoom",
      async (env) => {
        const track = getLocalCameraTrack(room);
        if (!track) return { ok: false, reason: "no local camera track" };
        try {
          const applied = await applyZoom(
            track.mediaStreamTrack,
            Number(env.payload.level),
          );
          // Re-broadcast capabilities — the `current` field of the zoom
          // range reflects the new level, so the operator UI's slider
          // tracks the actual device state (handy when multiple zoom
          // commands race or the camera clamps the requested value).
          void broadcastCapabilities();
          return { ok: true, level: applied };
        } catch (e) {
          return { ok: false, reason: (e as Error).message };
        }
      },
    );

    // Backlog 2026-09-18 — adaptive capture quality. LiveKit reports each
    // participant's outbound connection health as EXCELLENT/GOOD/POOR;
    // when the field device's OWN quality goes Poor, drop the capture
    // resolution (and, via the shared store, the snapshot JPEG quality)
    // to the "low" tier — then back to "high" once it recovers. Debounced
    // 4s in each direction so one blip doesn't flap the resolution.
    let qualityDebounce: ReturnType<typeof setTimeout> | undefined;
    let currentTier: CaptureQualityTier = "high";
    const onConnectionQualityChanged = (
      quality: ConnectionQuality,
      participant: { identity: string },
    ) => {
      if (participant.identity !== room.localParticipant.identity) return;
      const nextTier: CaptureQualityTier =
        quality === ConnectionQuality.Poor ? "low" : "high";
      if (nextTier === currentTier) {
        clearTimeout(qualityDebounce);
        return;
      }
      clearTimeout(qualityDebounce);
      qualityDebounce = setTimeout(() => {
        currentTier = nextTier;
        useConnectionQualityStore.getState().setTier(nextTier);
        void applyQualityTier(getLocalCameraTrack(room), nextTier);
      }, 4000);
    };
    room.on(RoomEvent.ConnectionQualityChanged, onConnectionQualityChanged);

    return () => {
      offSwitch();
      offTorch();
      offZoom();
      room.off("localTrackPublished", onPublished);
      room.off(
        RoomEvent.ConnectionQualityChanged,
        onConnectionQualityChanged,
      );
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(resumeTimer);
      clearTimeout(qualityDebounce);
      bus.dispose();
    };
  }, [room, sessionId]);

  return null;
}


async function handleSwitch(
  env: Envelope<SwitchCameraPayload>,
  room: ReturnType<typeof useRoomContext>,
): Promise<SwitchCameraAck> {
  const track = getLocalCameraTrack(room);
  if (!track) return { ok: false, reason: "no local camera track" };
  const settings = track.mediaStreamTrack.getSettings();
  const cur = (settings.facingMode as FacingMode | undefined) ?? "user";
  const requested = env.payload.facing;
  const next: FacingMode =
    requested === "toggle"
      ? cur === "user"
        ? "environment"
        : "user"
      : requested;
  try {
    const achieved = await switchFacing(track, next);
    return { ok: true, facing: achieved };
  } catch (e) {
    return { ok: false, reason: (e as Error).message };
  }
}


function getLocalCameraTrack(
  room: ReturnType<typeof useRoomContext>,
): LocalVideoTrack | null {
  if (!room) return null;
  const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
  const track = pub?.videoTrack;
  return track instanceof LocalVideoTrack ? track : null;
}